import React, { useEffect, useState, useRef } from "react";
import { Slider, Typography } from "@mhome/ui";
import { Air } from "@mui/icons-material";
import { SyncBadge } from "../../common/badges";
import FixedSelectInput from "../../input/fixed-select-input";

const FanDetail1Widget = ({ widget, onValueUpdate, pattern }) => {
  const [fanMode, setFanMode] = useState("");
  const [fanSpeed, setFanSpeed] = useState(0);
  const [modeOptions, setModeOptions] = useState([]);
  const [speedMinMax, setSpeedMinMax] = useState({ min: 0, max: 100 });
  const [widgetData, setWidgetData] = useState(null);
  const newSpeedRef = useRef(0);

  useEffect(() => {
    if (!widget) return;
    setWidgetData(widget);

    // Handle fan mode from $w1
    if (widget.items?.$w1) {
      const currentMode = widget.items.$w1.value || "";
      setFanMode(currentMode);

      // Set mode options from enums or provide defaults
      const options = widget.items.$w1.options || [];
      setModeOptions(options);
    }

    // Handle fan speed from $w2 (if present)
    if (widget.items?.$w2) {
      const currentSpeed = Number(widget.items.$w2.value) || 0;
      setFanSpeed(currentSpeed);
      setSpeedMinMax({
        min: widget.items.$w2.min ?? 0,
        max: widget.items.$w2.max ?? 100,
      });
      newSpeedRef.current = currentSpeed;
    }
  }, [widget]);

  const handleModeChange = (newMode) => {
    if (pattern && pattern.endsWith("_R")) return;

    setFanMode(newMode);

    if (widgetData?.items?.$w1) {
      const updatedWidget = {
        ...widgetData.items.$w1,
        value: newMode,
      };
      onValueUpdate(updatedWidget);
    }
  };

  const handleSpeedChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    if (!widgetData?.items?.$w2) return; // Don't handle speed changes if speed widget doesn't exist

    const adjustedValue = Math.min(
      Math.max(newValue, speedMinMax.min),
      speedMinMax.max
    );
    setFanSpeed(adjustedValue);
    newSpeedRef.current = adjustedValue;
  };

  const handleSpeedChangeCommitted = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;
    if (!widgetData?.items?.$w2) return;

    const updatedWidget = {
      ...widgetData.items.$w2,
      value: newSpeedRef.current,
    };
    onValueUpdate(updatedWidget);
  };

  const getFanIcon = () => {
    const isActive = fanMode && fanMode !== "off";
    return (
      <Air
        style={{
          fontSize: "1.5rem",
          color: "white",
        }}
        className={isActive ? "animate-spin" : ""}
      />
    );
  };

  const disabled = pattern && pattern.endsWith("_R");
  const hasSpeedControl = !!widgetData?.items?.$w2;
  const isActive = fanMode && fanMode !== "off";

  if (!widgetData) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-xs">Loading...</span>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      {pattern && pattern.endsWith("_W") && (
        <div className="flex justify-end">
          <SyncBadge />
        </div>
      )}

      {/* Fan Status Card */}
      <div
        className="rounded-2xl px-3 py-2 flex items-center justify-between transition-opacity duration-300"
        style={{
          height: "60px",
          backgroundColor: "hsl(var(--primary))",
          opacity: isActive ? 1 : 0.3,
        }}
      >
        <div className="flex flex-col">
          <Typography className="text-white font-bold capitalize">
            {fanMode || "Off"}
          </Typography>
          {hasSpeedControl && (
            <Typography className="text-white text-xs opacity-80">
              Speed: {fanSpeed}%
            </Typography>
          )}
        </div>
        <div className="h-10 aspect-square flex items-center justify-center flex-shrink-0">
          {getFanIcon()}
        </div>
      </div>

      {/* Fan Mode Selection */}
      <div className="flex flex-col gap-1">
        <Typography className="font-bold">Fan Mode</Typography>
        <div style={{ height: "48px" }}>
          <FixedSelectInput
            valueSpec={{
              value: fanMode,
              enums: modeOptions,
              readOnly: disabled,
            }}
            onChange={handleModeChange}
            onError={() => {}}
          />
        </div>
      </div>

      {/* Fan Speed Control */}
      {hasSpeedControl && (
        <div className="flex flex-col gap-1">
          <Typography className="font-bold">Fan Speed - {fanSpeed}%</Typography>
          <div className="px-1">
            <Slider
              value={fanSpeed}
              onChange={handleSpeedChange}
              onChangeCommitted={handleSpeedChangeCommitted}
              min={speedMinMax.min}
              max={speedMinMax.max}
              step={1}
              disabled={disabled}
              sx={{
                "& .MuiSlider-thumb": {
                  width: 20,
                  height: 20,
                },
                "& .MuiSlider-track": {
                  height: 6,
                },
                "& .MuiSlider-rail": {
                  height: 6,
                },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FanDetail1Widget;
